/**
 * Apertura de sesión contra el canal público.
 *
 * La llama el loader, desde la página del cliente, y el motivo es de seguridad: el `Origin` de la
 * petición es el del documento que la hace, así que solo desde ahí el servidor puede comprobar en
 * qué web está puesto el widget contra su lista de dominios permitidos.
 */

import { WidgetError, type EmbedSessionRequest, type SessionRequest, type SessionResponse } from './api';

const API_BASE = __ATLAS_API_BASE__;

/**
 * Abre la sesión y devuelve todo lo necesario para conversar.
 *
 * @param visitorToken Identidad guardada de una visita anterior, si la hay.
 */
export async function startSession(publicId: string, visitorToken?: string): Promise<SessionResponse> {
  const body: SessionRequest = { VisitorToken: visitorToken };

  let response: Response;

  try {
    response = await fetch(`${API_BASE}/widget/${encodeURIComponent(publicId)}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch {
    throw new WidgetError('network', 'No se ha podido conectar.');
  }

  if (!response.ok) {
    throw errorForSession(response.status, await readErrorName(response));
  }

  return (await response.json()) as SessionResponse;
}

/**
 * Abre la sesión del chat incrustado. La pide el propio panel desde el iframe, sin loader: el `Origin`
 * es el nuestro, y la web que lo contiene va declarada en el cuerpo. El servidor solo la acepta si
 * viene del panel y si esa web está entre los dominios de la publicación.
 *
 * @param embedderOrigin Origen de la web que contiene el iframe, leído del navegador.
 * @param visitorToken Identidad guardada de una visita anterior, si la hay.
 */
export async function startEmbedSession(
  publicId: string,
  embedderOrigin: string,
  visitorToken?: string
): Promise<SessionResponse> {
  const body: EmbedSessionRequest = { VisitorToken: visitorToken, EmbedderOrigin: embedderOrigin };

  let response: Response;

  try {
    response = await fetch(`${API_BASE}/widget/${encodeURIComponent(publicId)}/embed-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch {
    throw new WidgetError('network', 'No se ha podido conectar.');
  }

  if (!response.ok) {
    throw errorForSession(response.status, await readErrorName(response));
  }

  return (await response.json()) as SessionResponse;
}

function errorForSession(status: number, errorName?: string): WidgetError {
  if (errorName === 'WidgetNotAvailableException' || status === 403 || status === 404) {
    return new WidgetError('unavailable', 'El chat no está disponible en este momento.');
  }

  if (errorName === 'MonthlyQueryQuotaExceededException') {
    return new WidgetError('throttled', 'El servicio no está disponible en este momento.');
  }

  return new WidgetError('network', 'No se ha podido conectar. Inténtalo de nuevo.');
}

/**
 * Lee el nombre de la excepción del cuerpo del error, si lo trae.
 *
 * Tolerante con la forma de la propiedad: el cuerpo lo componen dos middlewares distintos según la
 * ruta.
 */
export async function readErrorName(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as Record<string, unknown>;

    for (const key of ['errorName', 'ErrorName', 'name', 'Name']) {
      const value = body[key];
      if (typeof value === 'string' && value.length > 0) return value;
    }
  } catch {
    // Un error sin cuerpo o con cuerpo ilegible se traduce solo por su código.
  }

  return undefined;
}
